import { useEffect, useState } from "react";
import { BookOpen, Users, ClipboardList, Save, CalendarCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { PanelAsistencia } from "@/components/PanelAsistencia";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function DocenteMaterias() {
  const { config } = useAuth();
  const PERIODOS = config.periodos;
  const [materias, setMaterias] = useState(null);
  const [sel, setSel] = useState(null);
  const [periodo, setPeriodo] = useState("");
  const [notas, setNotas] = useState(null);
  const [vista, setVista] = useState("notas");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/docente/materias").then((r) => setMaterias(r.data)).catch(() => setMaterias([]));
  }, []);

  const abrir = async (m, per = periodo || PERIODOS[0]) => {
    setSel(m);
    setPeriodo(per);
    setNotas(null);
    try {
      const { data } = await api.get(`/docente/materias/${m.id}/notas`, { params: { periodo: per } });
      setNotas(data.estudiantes);
    } catch (e) {
      toast.error(apiError(e));
      setNotas([]);
    }
  };

  const guardar = async () => {
    const items = notas
      .filter((n) => n.nota !== null && n.nota !== "" && !Number.isNaN(Number(n.nota)))
      .map((n) => ({ estudiante_id: n.id, nota: Number(n.nota), observaciones: n.observaciones || null }));
    if (items.length === 0) return toast.error("Ingrese al menos una nota");
    setBusy(true);
    try {
      const { data } = await api.post("/docente/notas",
                                      { materia_id: sel.id, periodo: periodo || PERIODOS[0], items });
      toast.success(`${data.guardadas} nota(s) publicada(s) en ${periodo}`);
      api.get("/docente/materias").then((r) => setMaterias(r.data));
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!materias) return <Loading />;

  return (
    <div data-testid="docente-materias">
      <PageTitle title="Mis materias"
                 subtitle="Publique calificaciones y marque la asistencia de los estudiantes en sus materias." />

      {materias.length === 0 ? (
        <Empty testId="sin-materias">
          Aún no tiene materias asignadas. Solicite a la administración que le asigne sus materias.
        </Empty>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="lista-materias-docente">
            {materias.map((m) => (
              <button key={m.id} onClick={() => abrir(m)} data-testid={`materia-${m.id}`}
                      className={`lift rounded-lg border bg-card p-6 text-left ${
                        sel?.id === m.id ? "border-primary ring-2 ring-primary/20" : "border-border/60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 text-primary">
                    <BookOpen className="h-4 w-4" />
                    <h3 className="font-display text-base font-bold">{m.nombre}</h3>
                  </div>
                  <Badge variant="outline">{m.grado}</Badge>
                </div>
                <div className="mt-5 flex gap-6 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {m.total_estudiantes} estudiantes</span>
                  <span className="flex items-center gap-1"><ClipboardList className="h-3 w-3" /> {m.total_notas} notas</span>
                </div>
              </button>
            ))}
          </div>

          {sel && (
            <div className="mt-10 rounded-lg border border-border/60 bg-card p-6 sm:p-8" data-testid="panel-notas">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {vista === "notas" ? "Publicar calificaciones" : "Registro de asistencia"}
                  </p>
                  <h2 className="mt-2 font-display text-2xl font-bold tracking-tight">
                    {sel.nombre} · {sel.grado}
                  </h2>
                </div>
                {vista === "notas" && (
                  <select value={periodo || PERIODOS[0]} onChange={(e) => abrir(sel, e.target.value)}
                          data-testid="selector-periodo"
                          className="rounded-full border border-border bg-background px-4 py-2 text-sm">
                    {PERIODOS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                )}
              </div>

              <div className="mt-6 flex gap-2">
                <Button size="sm" variant={vista === "notas" ? "default" : "outline"} className="rounded-full"
                        onClick={() => setVista("notas")} data-testid="tab-calificaciones">
                  <ClipboardList className="mr-2 h-3 w-3" /> Calificaciones
                </Button>
                <Button size="sm" variant={vista === "asistencia" ? "default" : "outline"} className="rounded-full"
                        onClick={() => setVista("asistencia")} data-testid="tab-asistencia">
                  <CalendarCheck className="mr-2 h-3 w-3" /> Asistencia
                </Button>
              </div>

              {vista === "asistencia" ? <PanelAsistencia materia={sel} /> : !notas ? (
                <Loading label="Cargando estudiantes…" />
              ) : notas.length === 0 ? (
                <Empty testId="sin-estudiantes">No hay estudiantes activos en {sel.grado}.</Empty>
              ) : (
                <>
                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full min-w-[620px] text-sm" data-testid="tabla-notas">
                      <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3">Estudiante</th>
                          <th className="px-4 py-3">Matrícula</th>
                          <th className="w-28 px-4 py-3">Nota</th>
                          <th className="px-4 py-3">Observaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notas.map((n, idx) => (
                          <tr key={n.id} className="border-t border-border/60">
                            <td className="px-4 py-3 font-medium">{n.nombre}</td>
                            <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{n.matricula}</td>
                            <td className="px-4 py-3">
                              <Input type="number" min="0" max="100" value={n.nota ?? ""}
                                     data-testid={`nota-${n.matricula}`}
                                     onChange={(e) => {
                                       const copy = [...notas];
                                       copy[idx] = { ...n, nota: e.target.value };
                                       setNotas(copy);
                                     }} />
                            </td>
                            <td className="px-4 py-3">
                              <Input value={n.observaciones ?? ""} placeholder="Comentario para el tutor"
                                     data-testid={`obs-${n.matricula}`}
                                     onChange={(e) => {
                                       const copy = [...notas];
                                       copy[idx] = { ...n, observaciones: e.target.value };
                                       setNotas(copy);
                                     }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button className="mt-6 rounded-full" onClick={guardar} disabled={busy}
                          data-testid="guardar-notas">
                    <Save className="mr-2 h-4 w-4" /> {busy ? "Publicando…" : "Publicar calificaciones"}
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
